import type { FC } from 'react';
import { SeriesAvailability } from '~/common/types/series-availability';
import type { StoryOverview } from '~/common/types/story-overview';
import { FreeBadge } from '@app/_components/free-badge/free-badge';
import { Likes } from '@app/_components/likes';
import { Link } from '@app/_components/link';
import { VipOnlyBadge } from '@app/_components/vip-only-badge';
import { css } from '@styled-system/css';
import { Paragraph, Box, HStack } from '@styled-system/jsx';
import { SensitiveFlag } from './sensitive-flag';

type Props = {
  story: StoryOverview;
  disableLink?: boolean;
};

export const EpisodeItem: FC<Props> = ({ story, disableLink }) => {
  const link = `/s/${story.id}`;
  const isFree = !story.isVipOnly && story.isOfficial && !story.isWaitRequired;
  const isNofollow =
    story.isSensitive || story.seriesAvailability !== SeriesAvailability.PUBLIC;

  return (
    <li
      className={css({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        _hover: {
          background: 'strong_bg',
          borderRadius: '8px',
        },
      })}
    >
      <Link
        href={disableLink ? '' : link}
        rel={isNofollow ? 'nofollow' : undefined}
      >
        <Box display="flex" flex-direction="row" alignItems="center">
          <span
            className={css({
              fontSize: '13px',
              fontWeight: '400',
              color: 'accent_500',
              marginRight: '4px',
              marginTop: '2px',
            })}
          >
            {story.seriesIndex ?? 1}.
          </span>

          {story.isVipOnly && <VipOnlyBadge />}
          {isFree && <FreeBadge />}
          <Paragraph fontSize="14px" fontWeight="bold" color="accent_700">
            {story.storyTitle}
          </Paragraph>
        </Box>
        <HStack
          mt="8px"
          display="flex"
          flex-direction="row"
          alignItems="center"
          justifyContent={{ base: 'left', lg: 'end' }}
        >
          <Box display={{ base: 'none', lg: 'block' }}>
            {story.isSensitive && <SensitiveFlag />}
          </Box>
          <span
            className={css({
              fontSize: '12px',
              lineHeight: '14px',
              color: 'accent.500',
            })}
          >
            {story.publishedAtText}
          </span>
          {(story.likeCount || 0) > 0 && (
            <Likes likesCount={story.likeCount} variants="normal" />
          )}
          <Box display={{ base: 'block', lg: 'none' }}>
            {story.isSensitive && <SensitiveFlag />}
          </Box>
        </HStack>
      </Link>
    </li>
  );
};
